import { hasFlag } from "country-flag-icons";
import getUnicodeFlagIcon from "country-flag-icons/unicode";
import { Icon, Typography } from "@/components/atoms";
import { FlightDetails, FlightLeg } from "@/redux/apis/clientFlight/types";
import { clsx } from "@/utils";
import { LandingSource, flightDuration, formatFlightDate, formatFlightTime, formatTerminal, legTime, resolveLanding } from "./flightPickup";

/** "AU" → 🇦🇺, guarded so a non-country code renders nothing rather than garbage indicators. */
const flagEmoji = (countryCode?: string | null): string => (countryCode && hasFlag(countryCode) ? getUnicodeFlagIcon(countryCode) : "");

/**
 * A leg's time, off the shared gate-first chain — the same one the footer and the engine's ArrivalResolver
 * read, so the two arrival slots on this card cannot name different events.
 *
 * Not a runway chain of its own, which is what a "wheels-down belongs in the timeline, the operative gate
 * arrival belongs in the footer" split would want. Cirium publishes no separate runway SCHEDULE, so before
 * live data `scheduled` IS the gate schedule and this slot holds a gate time; once live data lands
 * `estimated` is a touchdown ETA and the same slot, same styling, holds a different event. Which event a
 * client is reading would depend only on how close the flight is.
 *
 * No label either: RM-Client 5537-121254 has one arrival value in the leg row and the same one in the
 * footer. A label here would name a second event, and there is no second event. Where two arrivals do
 * remain on screen — a live one and the older one a booked pickup was built on — the footer discloses it
 * beside the numbers it reconciles, rather than as a word floating over one of them.
 */
const LegBlock = ({
  leg,
  align,
  hideTime,
}: {
  leg: FlightLeg | null;
  align: "start" | "end";
  /**
   * Drop the clock time, keeping the date and the airport. For a flight that is not operating: a confident
   * "6:05 PM SYD" one line above the word "Cancelled" is a contradiction, and the time is the only part of
   * the leg asserting something untrue. The route and the date still say WHICH flight instance the booking
   * was on, which is all the card is here to evidence.
   */
  hideTime?: boolean;
}) => {
  const time = legTime(leg);
  return (
    <div className={clsx("flex flex-col gap-2", align === "end" ? "items-end text-right" : "items-start")}>
      <div>
        {/* No em-dash placeholder when the time is deliberately withheld — the dash is for a time we
            expected and did not get, and here we are not claiming one at all. */}
        {!hideTime && (
          <Typography variant="title" className="block text-neutral-dark-gray">
            {time ? formatFlightTime(time) : "—"}
          </Typography>
        )}
        {time && (
          <Typography variant="small-regular" className="block font-medium text-neutral-dark-gray">
            {formatFlightDate(time)}
          </Typography>
        )}
      </div>
      {leg?.airport && (
        <Typography variant="action" className="text-neutral-silver-gray">
          {`${flagEmoji(leg.airport.countryCode)} ${leg.airport.iata}`.trim()}
        </Typography>
      )}
    </div>
  );
};

/**
 * Boarding-pass-style summary of a resolved flight (RM-Client 4002-24059): airline + number header,
 * departure → arrival legs in each airport's local time, and a single "Lands … • Terminal …" footer.
 * Purely presentational — the Date & Time panel owns fetching and the loading/not-found states.
 */
export const FlightDetailsCard = ({
  flight,
  frozenArrival,
  disruptionStatus,
  leg = "arrival",
}: {
  flight: FlightDetails;
  /**
   * The arrival the pickup ON SCREEN was derived from, already carrying the airport's offset. Present
   * only while that pickup is the booked one AND the two reconcile; once it re-derives, the caller passes
   * nothing and this card has one arrival to show and nothing to disclose. The pair moves together (D14).
   *
   * Rendered as the `booked from …` line under the footer's landing, not as the landing itself — see
   * `bookedFrom`. Absent on the create path, where there is no booked pickup and nothing to disclose.
   *
   * `source` has no reader: it distinguished the leg row's event from the footer's, and there is one event
   * now. Kept because the panel supplies it, and optional because the V2 customer payload does not yet
   * send `flight_pickup_derived_arrival_source`.
   */
  frozenArrival?: { iso: string; source?: LandingSource | null } | null;
  /**
   * A cancelled or diverted flight. Takes the arrival line's place in the footer — `Cancelled` where
   * `Lands 10:55 AM • Terminal 3` sits — rather than sitting beside it.
   *
   * Beside it, the two would read as one claim: a landing time under a flight that is not landing. The
   * pickup is not derived from anything here either, so the number the footer exists to justify has no
   * meaning on this card; the status is what the reader came for.
   *
   * Rendered even when no landing resolves, which is the ordinary case — a cancelled flight is usually
   * pulled from the schedule and has no arrival estimate left to show.
   */
  disruptionStatus?: "cancelled" | "diverted" | null;
  /**
   * Which end of the flight this card is about (DEV-1487). `"arrival"` — the default, and every caller
   * before this ticket — is the pickup's: the footer reads `Lands 10:55 AM • Terminal 3`. `"departure"`
   * is the drop-off's, where the operative event is the one the car has to beat, so the footer reads
   * `Departs 1:25 PM • Terminal 1` off the departure leg instead.
   *
   * The header, the leg row and the duration are identical either way — the card already draws the whole
   * flight, and a drop-off client wants the same boarding-pass summary. Only the footer names an event,
   * and naming the wrong one puts the arrival terminal at the far airport under a booking whose driver
   * needs the departure terminal here.
   */
  leg?: "arrival" | "departure";
}) => {
  // The one arrival on this card: the leg row resolves it the same way, and the footer prints it again.
  const live = resolveLanding(flight);
  // The span between the two times the card PRINTS, deliberately, and not the airborne leg.
  //
  // Running it runway-to-runway keeps taxi-in out of the number — right about an on-time 8h leg being
  // overstated as "8h 25m", wrong about this slot. The card never calls the number airborne time; it sits
  // between two clocks, and a reader who subtracts one from the other has to get it. RM-Client 5537-121254
  // reads it that way too: 11:25 AM → 1:25 PM is "2h".
  //
  // Gate-to-gate would give both, and does not exist: only the ARRIVAL leg carries gate times.
  // `FlightDeparture` has no gate columns and `Flight::$departureGate` is a gate NUMBER ("B12"). So the
  // departure end is the runway chain, which `legTime` falls through to on its own.
  //
  // The right-hand end is `live` rather than a second `legTime(flight.arrival)`: the same value, but taken
  // from the resolution the footer prints, so the span cannot end anywhere but where the card says it does.
  const duration = flightDuration(legTime(flight.departure), live?.iso ?? null);
  // The event the footer names, and the terminal that belongs to it. On the departure side there is no
  // gate chain to resolve — `FlightDeparture` carries no gate columns — so `legTime` falls through to
  // the runway tail on its own, which is the scheduled departure a client reads off their boarding pass.
  const departing = leg === "departure";
  const footerIso = departing ? legTime(flight.departure) : live?.iso ?? null;
  const terminal = departing ? flight.departure?.terminal : flight.arrival?.terminal;
  // The booked pickup's anchor, BENEATH the live landing rather than in place of it (Kevin, 1 Sep 2026).
  //
  // Giving `frozenArrival` the headline keeps the pickup and the arrival above it reconciling (D14), and
  // costs the client the number they came for: the card leads with the arrival the booking was built on
  // while the flight is now expected at the gate an hour later — the value on the airport board, in every
  // tracking app, and in the engine. Leading with the live one and keeping the anchor as a subordinate
  // line satisfies D14 too: the displayed pickup is still explainable, which is all this card owes it.
  //
  // Compared as rendered wall clocks, not instants, because that is what the reader compares. Two values
  // thirty seconds apart both read "6:45 AM", and "booked from 6:45 AM" under "Lands 6:45 AM" prints one
  // instant twice as if it were two facts.
  //
  // Blind to a date rollover by the same token: two arrivals exactly a day apart render the same clock and
  // no line appears — the line goes silent rather than printing a wrong date.
  //
  // This IS reachable. A live landing a day EARLIER than the stored one is elapsed by definition, and the
  // elapsed guard is what holds the anchor in place, so `frozenArrival` can carry a day-old value. The leg
  // row carries the live date, so the card is not wholly silent about it, but this line is. Fixing it needs
  // a date in this comparison and in the text it prints.
  const bookedFrom =
    !disruptionStatus && live && frozenArrival?.iso && formatFlightTime(frozenArrival.iso) !== formatFlightTime(live.iso)
      ? formatFlightTime(frozenArrival.iso)
      : undefined;

  return (
    <div className="w-full overflow-hidden rounded-lg border border-neutral-mid-gray bg-white">
      <div className="flex items-center justify-between border-b border-neutral-mid-gray p-3">
        <Typography variant="title" className="text-danger">
          {(flight.airline?.name ?? flight.number).toUpperCase()}
        </Typography>
        <Typography variant="title" className="text-neutral-dark-gray">
          {flight.number}
        </Typography>
      </div>

      <div className="flex items-center gap-5 p-3">
        <LegBlock leg={flight.departure} align="start" hideTime={!!disruptionStatus} />
        <div className="relative flex min-w-0 flex-1 flex-col items-center gap-1">
          <div className="flex w-full items-center gap-1 text-neutral-silver-gray">
            <span aria-hidden className="min-w-0 flex-1 border-t border-dashed border-neutral-silver-gray" />
            <Icon name="Airplane" size="sm" className="rotate-90" />
            <span aria-hidden className="min-w-0 flex-1 border-t border-dashed border-neutral-silver-gray" />
          </div>
          {/* Derived from the two times that were just withheld, so it asserts the same untruth. */}
          {!disruptionStatus && duration && (
            <Typography variant="small" className="text-neutral-dark-gray">
              {duration}
            </Typography>
          )}
        </div>
        <LegBlock leg={flight.arrival} align="end" hideTime={!!disruptionStatus} />
      </div>

      {(disruptionStatus || footerIso) && (
        <div className="border-t border-neutral-mid-gray bg-neutral-surface-gray p-3">
          <div className="flex items-center gap-2">
            <Icon name="Airplane" size="md" className="text-neutral-dark-gray" />
            {disruptionStatus ? (
              <Typography variant="title" className="text-danger">
                {disruptionStatus === "cancelled" ? "Cancelled" : "Diverted"}
              </Typography>
            ) : (
              <>
                {/* One neutral word whatever the source (RM-Client 4002-24059). "Lands" is true of a gate
                    schedule, a gate estimate and a touchdown alike, and a sharper label would tell the
                    reader nothing they act on. The one distinction that can still matter — this is not the
                    arrival your pickup was built on — is the `booked from` line below, stated as a time
                    rather than as an adjective. */}
                <Typography variant="muted" className="text-neutral-dark-gray">
                  {departing ? "Departs" : "Lands"}
                </Typography>
                <Typography variant="title" className="text-neutral-black">
                  {formatFlightTime(footerIso!)}
                </Typography>
                {terminal && (
                  <>
                    <span aria-hidden className="size-[5px] rounded-full bg-neutral-mid-gray" />
                    <Typography variant="muted" className="text-neutral-dark-gray">
                      {formatTerminal(terminal)}
                    </Typography>
                  </>
                )}
              </>
            )}
          </div>
          {/* Indented past the icon to the landing line's text (md Icon 20px + the row's 8px gap = pl-7),
              so it reads as a footnote to that number rather than a second claim of equal weight. Lower
              case and muted for the same reason: it answers "then why is my pickup not that time?" for the
              reader who asks it, and is ignorable by the one who does not. */}
          {bookedFrom && (
            <Typography variant="small-regular" className="mt-1 block pl-7 text-neutral-silver-gray">
              {`booked from ${bookedFrom}`}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};
